import {
  Drawer,
  Box,
  Typography,
  Grid2,
  Paper,
  CircularProgress,
} from "@mui/material";
import { Button, TextField, SelectBox } from "./../../common";
import { useValidateForm } from "./../../../hooks";
import { OneOffAddNewAccountSchema } from "./../../../validations";
import { US_STATES } from "../../../constants";
import moment from "moment";
import {
  useCreateAccountMutation,
  useUpdateProfileAndAddressMutation,
  useCreateContactMutation,
  useCreateAccountFinancialMutation,
  useCreateLocationMutation,
} from "../../../api";

type Props = {
  open: boolean;
  onClose: () => void;
  onAccountCreated: (accountId: number) => void;
  orgId: number;
};
export const CreateAccountDrawer = ({
  open,
  onClose,
  onAccountCreated,
  orgId,
}: Props) => {
  const { register, handleSubmit, errors } = useValidateForm(
    OneOffAddNewAccountSchema,
  );
  const handleCreateAccount = (data: any) => {
    createAccountMutation.mutate(
      {
        orgId,
        accountInfo: {
          accountName: data.companyName?.trim() || data.contactPerson.trim(),
          status: "pending",
        },
      },
      {
        onSuccess: (accountId: number) => {
          updateProfileMutation.mutate(
            {
              orgId,
              accountId,
              contactPerson: data.contactPerson,
              email: data.email,

              ...(data.contactNumber && { contactNumber: data.contactNumber }),
              ...(data.address1 && { addressLine1: data.address1 }),
              ...(data.address2 && { addressLine2: data.address2 }),
              ...(data.city && { city: data.city }),
              ...(data.state && { state: data.state }),
              ...(data.zip && { zip: data.zip }),
            },
            {
              onSuccess: () => {
                createContactMutation.mutate({
                  orgId,
                  accountId,
                  contact: {
                    id: 0,
                    title: data.contactPerson,
                    name: data.contactPerson,
                    email: data.email,
                    number:
                      data.contactNumber?.toString().trim() || "0000000000",
                    isEmailRequired: false,
                    isBillingNotificationsRequired: true,
                    isSmsRequired: false,
                    isLoginRequired: false,
                  },
                });

                createAccountFinancialMutation.mutate({
                  orgId,
                  accountId,
                  financialInfo: {
                    quickbooksId: "",
                    purchaseOrder: "",
                    invoiceFrequency: "daily",
                    invoiceDate: moment()
                      .startOf("day")
                      .utc()
                      .format("YYYY-MM-DD HH:mm:ss"),
                  },
                });

                createLocationMutation.mutate({
                  orgId,
                  accountId,
                  location: {
                    locationName: "Primary",
                    zip: "00000",
                    addressLine1: "N/A",
                    addressLine2: "N/A",
                    city: "N/A",
                    state: "N/A",
                  },
                });

                onAccountCreated(accountId);
                onClose();
              },
            },
          );
        },
      },
    );
  };
  const createAccountMutation = useCreateAccountMutation();
  const updateProfileMutation = useUpdateProfileAndAddressMutation();
  const createContactMutation = useCreateContactMutation();
  const createAccountFinancialMutation = useCreateAccountFinancialMutation();
  const createLocationMutation = useCreateLocationMutation();
  const isLoading =
    createAccountMutation.isLoading || updateProfileMutation.isLoading;

  return (
    <Drawer
      anchor="right"
      open={open}
      onClose={onClose}
      PaperProps={{
        sx: {
          width: { xs: "100%", sm: 520 },
          maxWidth: "100%",
        },
      }}
    >
      {isLoading && (
        <Box
          sx={{
            position: "absolute",
            inset: 0,
            backgroundColor: "rgba(255,255,255,0.6)",
            zIndex: 10,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          <CircularProgress />
        </Box>
      )}

      <Box
        component="form"
        onSubmit={handleSubmit(handleCreateAccount)}
        noValidate
        sx={{
          width: "100%",
          p: { xs: 2, md: 3 },
          position: "relative",
          "& .MuiFormLabel-asterisk": { color: "red" },
        }}
      >
        <Typography variant="h6" sx={{ mb: 2 }}>
          Profile
        </Typography>

        <Paper variant="outlined" sx={{ p: 2, mb: 3 }}>
          <Grid2 container spacing={2} alignItems="center">
            <Grid2 size={12}>
              <TextField
                register={register}
                label="Company Name"
                name="companyName"
                error={errors["companyName"]}
              />
            </Grid2>
          </Grid2>
        </Paper>

        <Paper variant="outlined" sx={{ p: 2 }}>
          <Grid2 container spacing={2}>
            <Grid2 size={{ xs: 12, md: 6 }}>
              <TextField
                register={register}
                label="Contact Person"
                name="contactPerson"
                error={errors["contactPerson"]}
                required
              />
            </Grid2>

            <Grid2 size={{ xs: 12, md: 6 }}>
              <TextField
                register={register}
                label="Address Line 1"
                name="address1"
                error={errors["address1"]}
              />
            </Grid2>

            <Grid2 size={{ xs: 12, md: 6 }}>
              <TextField
                register={register}
                label="Email"
                name="email"
                error={errors["email"]}
                required
              />
            </Grid2>

            <Grid2 size={{ xs: 12, md: 6 }}>
              <TextField
                register={register}
                label="Address Line 2"
                name="address2"
                error={errors["address2"]}
              />
            </Grid2>

            <Grid2 size={12}>
              <TextField
                type="number"
                register={register}
                label="Contact Number"
                name="contactNumber"
                error={errors["contactNumber"]}
                allowScientificNotation={false}
              />
            </Grid2>

            <Grid2 size={{ xs: 12, md: 4 }}>
              <TextField
                register={register}
                label="City"
                name="city"
                error={errors["city"]}
              />
            </Grid2>

            <Grid2 size={{ xs: 12, md: 4 }}>
              <SelectBox
                register={register}
                label="State"
                name="state"
                dropdown={US_STATES}
                error={errors["state"]}
              />
            </Grid2>

            <Grid2 size={{ xs: 12, md: 4 }}>
              <TextField
                type="number"
                register={register}
                label="Zip"
                name="zip"
                error={errors["zip"]}
              />
            </Grid2>
          </Grid2>
        </Paper>

        <Box
          sx={{
            mt: 3,
            display: "flex",
            flexDirection: "row",
            justifyContent: "flex-end",
            gap: 2,
            flexWrap: "wrap",
          }}
        >
          <Button label="Cancel" variantType="secondary" onClick={onClose} />

          <Button
            label={isLoading ? "Saving..." : "Save"}
            variantType="primary"
            onClick={handleSubmit(handleCreateAccount)}
            disabled={isLoading}
          />
        </Box>
      </Box>
    </Drawer>
  );
};
